import { useMemo } from "react";

import { Observation } from "@medplum/fhirtypes";

import { Group, List, Table } from "@mantine/core";

import { TextWrapper } from "@/components/TextWrapper";
import ActionCell from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/Common/ActionCell";
import LastUpdated from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/Common/LastUpdated";
import PreviousVersions from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/Common/PreviousVersions";
import classes from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/Common/PreviousVersions.module.css";
import TimeCell from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/Common/TimeCell";
import { hasPreviousVersions } from "@/encounters/components/EncounterLabor/services/labor";
import { getMedsTherapiesRowValues } from "@/encounters/components/EncounterLabor/utils/labor";
import { usDateTimeFormat } from "@/utils/datetime";

interface MedsAndTherapiesRowProps {
  time: string;
  observation: Observation;
  showNumbersOnly: boolean;
  showPreviousVersions?: boolean;
  isViewOnly?: boolean;
}

export default function MedsAndTherapiesRow({
  time,
  observation,
  showNumbersOnly,
  showPreviousVersions = true,
  isViewOnly = false,
}: MedsAndTherapiesRowProps) {
  const previousVersions = hasPreviousVersions(observation);

  const { medications, therapies, ivAccess, fluidVolume, notes, hasAnswers, hasNotes } = useMemo(
    () => getMedsTherapiesRowValues(observation),
    [observation],
  );

  if ((!hasAnswers && !hasNotes) || showNumbersOnly) return null;
  const hasMedications = medications && medications.some((med) => med.medication);
  const hasTherapies = therapies && therapies.some((therapy) => therapy.therapy);

  return (
    <Table.Tr bg="white" className={classes.tableRow}>
      {showPreviousVersions && (
        <TimeCell time={time} rowSpan={1} isViewOnly={isViewOnly} observation={observation} />
      )}
      <Table.Td p="md" colSpan={10}>
        {hasAnswers && (
          <>
            <TextWrapper size="sm">Meds & Therapies:</TextWrapper>
            <List fz="sm" withPadding>
              {hasMedications && (
                <List.Item>
                  <TextWrapper size="sm" variant="bold">
                    Medication:
                  </TextWrapper>
                  <List withPadding type="ordered">
                    {medications?.map((med, index) =>
                      med.medication ? (
                        <List.Item key={`med-${index}`}>
                          <TextWrapper size="sm" variant="bold">
                            {med.medication.charAt(0).toUpperCase() + med.medication.slice(1)}:
                          </TextWrapper>
                          <List withPadding>
                            {med.form && (
                              <List.Item>
                                <Group gap="xs">
                                  <TextWrapper size="sm" variant="bold">
                                    Form -
                                  </TextWrapper>
                                  <TextWrapper size="sm">{med.form}</TextWrapper>
                                </Group>
                              </List.Item>
                            )}
                            {med.dosage && (
                              <List.Item>
                                <Group gap="xs">
                                  <TextWrapper size="sm" variant="bold">
                                    Dosage -
                                  </TextWrapper>
                                  <TextWrapper size="sm">{med.dosage}</TextWrapper>
                                </Group>
                              </List.Item>
                            )}
                            {med.frequency && (
                              <List.Item>
                                <Group gap="xs">
                                  <TextWrapper size="sm" variant="bold">
                                    Frequency -
                                  </TextWrapper>
                                  <TextWrapper size="sm">{med.frequency}</TextWrapper>
                                </Group>
                              </List.Item>
                            )}
                            {med.duration && (
                              <List.Item>
                                <Group gap="xs">
                                  <TextWrapper size="sm" variant="bold">
                                    Duration -
                                  </TextWrapper>
                                  <TextWrapper size="sm">
                                    {med.duration === "X days" && med.xDays
                                      ? `${med.xDays} Days`
                                      : med.duration}
                                  </TextWrapper>
                                </Group>
                              </List.Item>
                            )}
                            {med.prn && (
                              <List.Item>
                                <Group gap="xs">
                                  <TextWrapper size="sm" variant="bold">
                                    PRN -
                                  </TextWrapper>
                                  <TextWrapper size="sm">{med.prn}</TextWrapper>
                                </Group>
                              </List.Item>
                            )}
                            {med.specialInstructions && (
                              <List.Item>
                                <Group gap="xs">
                                  <TextWrapper size="sm" variant="bold">
                                    Special Instructions -
                                  </TextWrapper>
                                  <TextWrapper size="sm">{med.specialInstructions}</TextWrapper>
                                </Group>
                              </List.Item>
                            )}
                          </List>
                        </List.Item>
                      ) : null,
                    )}
                  </List>
                </List.Item>
              )}
              {hasTherapies && (
                <List.Item>
                  <TextWrapper size="sm" variant="bold">
                    Therapy:
                  </TextWrapper>
                  <List withPadding type="ordered">
                    {therapies?.map((therapy, index) =>
                      therapy.therapy ? (
                        <List.Item key={`therapy-${index}`}>
                          <TextWrapper size="sm" variant="bold">
                            {therapy.therapy.charAt(0).toUpperCase() + therapy.therapy.slice(1)}:
                          </TextWrapper>
                          {therapy.reason && (
                            <List withPadding>
                              <List.Item>
                                <Group gap="xs">
                                  <TextWrapper size="sm" variant="bold">
                                    Reason -
                                  </TextWrapper>
                                  <TextWrapper size="sm">{therapy.reason}</TextWrapper>
                                </Group>
                              </List.Item>
                            </List>
                          )}
                        </List.Item>
                      ) : null,
                    )}
                  </List>
                </List.Item>
              )}
              {ivAccess && (
                <List.Item>
                  <Group gap="xs">
                    <TextWrapper size="sm" variant="bold">
                      IV Access:
                    </TextWrapper>
                    <TextWrapper size="sm">{usDateTimeFormat(ivAccess)}</TextWrapper>
                  </Group>
                </List.Item>
              )}
              {fluidVolume && (
                <List.Item>
                  <Group gap="xs">
                    <TextWrapper size="sm" variant="bold">
                      Fluid Volume:
                    </TextWrapper>
                    <TextWrapper size="sm">{fluidVolume} mL</TextWrapper>
                  </Group>
                </List.Item>
              )}
            </List>
          </>
        )}
        {hasNotes && (
          <TextWrapper pt="xs" size="sm">
            Meds & Therapies Comments: {notes}
          </TextWrapper>
        )}
        {showPreviousVersions && previousVersions ? (
          <PreviousVersions
            observation={observation}
            rowComponent={MedsAndTherapiesRow}
            showPreviousVersions={showPreviousVersions}
          />
        ) : (
          <LastUpdated observation={observation} showPreviousVersions={false} />
        )}
      </Table.Td>
      {showPreviousVersions && !isViewOnly && <ActionCell rowSpan={1} observation={observation} />}
    </Table.Tr>
  );
}
